import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchMyApplications } from "../lib/api";
import { useAuth } from "../lib/auth";
import { categoryFor } from "../lib/categories";
import { CategoryIcon } from "../lib/categoryIcons";
import { X, ArrowLeft, ExternalLink, Briefcase, MapPin, DollarSign } from "lucide-react";
import GoogleButton from "./GoogleButton";

function fmt(s) {
    if (!s) return "";
    return String(s).slice(0, 10);
}

export default function MyApplicationsPage({ onClose }) {
    const { user, loading: authLoading, login } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const list = await fetchMyApplications();
                setItems(list);
            } finally {
                setLoading(false);
            }
        })();
    }, [user, authLoading]);

    if (!authLoading && !user) {
        return (
            <div className="fixed inset-0 z-[600] bg-white flex items-center justify-center p-6" data-testid="my-apps-gate">
                <div className="max-w-md w-full text-center">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-[#ffdccb] text-[#e85812] flex items-center justify-center mb-4">
                        <Briefcase className="w-7 h-7" />
                    </div>
                    <h1 className="font-display font-extrabold text-2xl text-[#1a1e2c] mb-2">
                        Sign in to see your applications
                    </h1>
                    <p className="text-slate-500 font-body text-[14px] mb-5">
                        Sign in with Google to keep track of every job you apply to and to skip the form next time.
                    </p>
                    <GoogleButton onClick={login} label="Continue with Google" />
                    <button className="mt-4 text-sm text-slate-500 hover:text-[#e85812] underline" onClick={onClose}>
                        Back to jobs
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[600] bg-white overflow-auto" data-testid="my-apps-page">
            <div className="max-w-3xl mx-auto p-5 md:p-8">
                <div className="flex items-center justify-between mb-4">
                    <button className="btn-ghost !py-2 !px-3" onClick={onClose} data-testid="my-apps-back">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button className="btn-ghost !py-2 !px-3" onClick={onClose} aria-label="close" data-testid="my-apps-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="mb-6">
                    <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#1a1e2c]">
                        My applications
                    </h1>
                    <p className="text-slate-500 font-body text-sm mt-0.5">
                        {items.length} job{items.length === 1 ? "" : "s"} you have applied to — most recent first.
                    </p>
                </div>

                {loading && <div className="text-slate-500 text-sm">Loading your applications…</div>}
                {!loading && items.length === 0 && (
                    <div className="card-soft p-8 text-center">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-[#fff2ea] text-[#e85812] flex items-center justify-center mb-3">
                            <Briefcase className="w-7 h-7" />
                        </div>
                        <div className="font-display font-semibold text-[#1a1e2c] text-lg">
                            No applications yet
                        </div>
                        <p className="text-slate-500 text-sm mt-1 max-w-xs mx-auto">
                            When you tap a pin and apply, the job will appear here — you can always come back to check the employer&apos;s response page.
                        </p>
                        <button onClick={onClose} className="btn-primary mt-5" data-testid="my-apps-browse-btn">
                            Browse jobs
                        </button>
                    </div>
                )}
                <div className="flex flex-col gap-3">
                    {items.map((r, i) => {
                        const catId = r.cat || "_default";
                        const cat = categoryFor(catId);
                        return (
                            <motion.div
                                key={`${r.job_id || i}-${r.created_at || i}`}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="card-soft p-4 flex items-start gap-3"
                                data-testid={`my-app-row-${r.job_id || i}`}
                            >
                                <div
                                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                                    style={{ background: cat.color }}
                                >
                                    <CategoryIcon catId={cat.id} size={22} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-display font-semibold text-[15px] text-[#1a1e2c] truncate">
                                        {r.job_title || "Job"}
                                    </div>
                                    <div className="text-[13px] text-slate-600 truncate">
                                        {r.company || "Employer"}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[12px] text-slate-500">
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {r.city || "—"}
                                        </span>
                                        <span>Applied {fmt(r.created_at)}</span>
                                    </div>
                                </div>
                                {r.job_url && (
                                    <a
                                        href={r.job_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-ghost !py-2 !px-3 !text-xs shrink-0"
                                        data-testid={`my-app-open-${r.job_id || i}`}
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" /> Open
                                    </a>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
